import { useState, useMemo } from 'react'
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Checkbox } from './ui/checkbox'
import { mdiArrowLeft, mdiContentSave } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useCreateComplianceControl, useComplianceControls, COMPLIANCE_DOMAINS } from '../hooks/useComplianceApi'
import { useDebounce } from '../hooks/useDebounce'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useToast } from '../hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'

export function CreateComplianceControlPage() {
  const { org } = useParams({ strict: false })
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t } = useTranslation('compliance')

  const createMutation = useCreateComplianceControl(org || 'demo')
  const { data: allControls = [] } = useComplianceControls(org || 'demo')

  const [code, setCode] = useState('')
  const [domain, setDomain] = useState<string>(COMPLIANCE_DOMAINS[0])
  const [nameEn, setNameEn] = useState('')
  const [nameFr, setNameFr] = useState('')
  const [descriptionEn, setDescriptionEn] = useState('')
  const [descriptionFr, setDescriptionFr] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [position, setPosition] = useState(0)

  const debouncedCode = useDebounce(code, 300)
  const codeDuplicate = useMemo(() => {
    if (!debouncedCode.trim()) return false
    return allControls.some(c => c.code === debouncedCode.trim())
  }, [debouncedCode, allControls])

  const breadcrumbs = useMemo(() => [
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.org_settings'), path: `/${org}/settings` },
    { title: t('breadcrumb.compliance'), path: `/${org}/compliance-controls` },
    { title: t('breadcrumb.create_control') }
  ], [org, t])
  useBreadcrumbs(breadcrumbs)

  const handleSave = async () => {
    if (!code.trim() || !nameEn.trim()) return

    const name: Record<string, string> = { en: nameEn }
    if (nameFr.trim()) name.fr = nameFr

    const description: Record<string, string> = {}
    if (descriptionEn.trim()) description.en = descriptionEn
    if (descriptionFr.trim()) description.fr = descriptionFr

    try {
      const result = await createMutation.mutateAsync({
        code: code.trim(),
        domain,
        name,
        description: Object.keys(description).length > 0 ? description : undefined,
        enabled,
        position,
      } as any)

      navigate({
        to: '/$org/compliance-controls/$controlUid',
        params: { org: org || 'demo', controlUid: result.uid }
      })
    } catch (error) {
      toast({
        title: t('common:status.error'),
        description: (error as any)?.title || t('controls.error_create'),
        variant: 'destructive'
      })
    }
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="space-y-6">
        <PageHeader
          icon={SECTION_ICONS.compliance}
          title={t('controls.create_title')}
          description={t('controls.create_subtitle')}
          actions={
            <Link to="/$org/compliance-controls" params={{ org: org || 'demo' }} search={{ domain_code: undefined }}>
              <Button variant="ghost" data-testid="back-btn">
                <AppIcon path={mdiArrowLeft} size="sm" className="mr-2" />
                {t('common:actions.back')}
              </Button>
            </Link>
          }
        />

        <Card>
          <CardHeader>
            <CardTitle>{t('controls.form_title')}</CardTitle>
            <CardDescription>{t('controls.create_form_description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">{t('controls.field_code')} *</Label>
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))}
                  placeholder={t('controls.placeholder_code')}
                  data-testid="code-input"
                />
                {codeDuplicate && (
                  <p className="text-xs text-destructive" data-testid="code-duplicate-error">
                    {t('controls.code_already_exists')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="domain">{t('controls.field_domain')} *</Label>
                <select
                  id="domain"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs"
                  data-testid="domain-select"
                >
                  {COMPLIANCE_DOMAINS.map(d => (
                    <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name-en">{t('controls.field_name_en')} *</Label>
              <Input id="name-en" value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder={t('controls.placeholder_name_en')} data-testid="name-en-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name-fr">{t('controls.field_name_fr')}</Label>
              <Input id="name-fr" value={nameFr} onChange={(e) => setNameFr(e.target.value)} placeholder={t('controls.placeholder_name_fr')} data-testid="name-fr-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description-en">{t('controls.field_description_en')}</Label>
              <Textarea id="description-en" value={descriptionEn} onChange={(e) => setDescriptionEn(e.target.value)} placeholder={t('controls.placeholder_description_en')} rows={3} data-testid="description-en-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description-fr">{t('controls.field_description_fr')}</Label>
              <Textarea id="description-fr" value={descriptionFr} onChange={(e) => setDescriptionFr(e.target.value)} placeholder={t('controls.placeholder_description_fr')} rows={3} data-testid="description-fr-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox id="enabled" checked={enabled} onCheckedChange={setEnabled} data-testid="enabled-switch" />
                <Label htmlFor="enabled">{t('controls.field_enabled')}</Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">{t('controls.field_position')}</Label>
                <Input id="position" type="number" value={position} onChange={(e) => setPosition(parseInt(e.target.value) || 0)} data-testid="position-input" />
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button
                onClick={handleSave}
                disabled={createMutation.isPending || !code.trim() || !nameEn.trim() || codeDuplicate}
                data-testid="create-control-submit"
              >
                <AppIcon path={mdiContentSave} size="sm" className="mr-2" />
                {t('controls.create_submit')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
